import React, { useState } from 'react';
import { SearchIcon, PlusIcon, CalendarIcon, CheckCircleIcon, ClockIcon, XIcon, AlertTriangleIcon, BellIcon, CheckIcon } from 'lucide-react';
export function ContractMilestones({
  locale
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [formData, setFormData] = useState({
    contractId: '',
    title: '',
    description: '',
    dueDate: '',
    completionDate: '',
    status: 'Pending',
    reminderDays: '7',
    notifyUsers: []
  });
  // Sample milestones data
  const milestones = [{
    id: 'MLS-2023-001',
    contractId: 'CTR-2023-002',
    contractName: 'IT Support Services',
    vendor: 'TechPro Solutions',
    title: 'Initial Setup Complete',
    description: 'Complete initial system setup and configuration',
    dueDate: '2023-03-15',
    completionDate: '2023-03-12',
    status: 'Completed',
    reminderDays: 7,
    notifyUsers: ['user1@example.com', 'user2@example.com']
  }, {
    id: 'MLS-2023-002',
    contractId: 'CTR-2023-002',
    contractName: 'IT Support Services',
    vendor: 'TechPro Solutions',
    title: 'First Quarterly Review',
    description: 'Conduct first quarterly service review meeting',
    dueDate: '2023-05-01',
    completionDate: '2023-05-03',
    status: 'Completed',
    reminderDays: 7,
    notifyUsers: ['user1@example.com', 'user3@example.com']
  }, {
    id: 'MLS-2023-003',
    contractId: 'CTR-2023-002',
    contractName: 'IT Support Services',
    vendor: 'TechPro Solutions',
    title: 'Second Quarterly Review',
    description: 'Conduct second quarterly service review meeting',
    dueDate: '2023-08-01',
    completionDate: '',
    status: 'Upcoming',
    reminderDays: 14,
    notifyUsers: ['user1@example.com', 'user3@example.com']
  }, {
    id: 'MLS-2023-004',
    contractId: 'CTR-2023-001',
    contractName: 'Office Lease Agreement',
    vendor: 'Skyline Properties Inc.',
    title: 'Annual Inspection',
    description: 'Complete annual property inspection',
    dueDate: '2023-12-15',
    completionDate: '',
    status: 'Upcoming',
    reminderDays: 30,
    notifyUsers: ['user4@example.com']
  }, {
    id: 'MLS-2023-005',
    contractId: 'CTR-2023-006',
    contractName: 'Consulting Services',
    vendor: 'Business Consultants Ltd.',
    title: 'Phase 1 Deliverable',
    description: 'Submit initial assessment report',
    dueDate: '2023-05-30',
    completionDate: '',
    status: 'Overdue',
    reminderDays: 7,
    notifyUsers: ['user1@example.com', 'user5@example.com']
  }, {
    id: 'MLS-2023-006',
    contractId: 'CTR-2023-010',
    contractName: 'Cloud Infrastructure',
    vendor: 'AWS',
    title: 'Migration Complete',
    description: 'Complete migration to new cloud infrastructure',
    dueDate: '2023-09-30',
    completionDate: '',
    status: 'At Risk',
    reminderDays: 14,
    notifyUsers: ['user6@example.com', 'user7@example.com']
  }];
  // Filter milestones based on search term
  const filteredMilestones = milestones.filter(milestone => milestone.id.toLowerCase().includes(searchTerm.toLowerCase()) || milestone.contractId.toLowerCase().includes(searchTerm.toLowerCase()) || milestone.contractName.toLowerCase().includes(searchTerm.toLowerCase()) || milestone.vendor.toLowerCase().includes(searchTerm.toLowerCase()) || milestone.title.toLowerCase().includes(searchTerm.toLowerCase()) || milestone.description.toLowerCase().includes(searchTerm.toLowerCase()));
  // Get status badge color
  const getStatusColor = status => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'Overdue':
        return 'bg-red-100 text-red-800';
      case 'At Risk':
        return 'bg-yellow-100 text-yellow-800';
      case 'Pending':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status icon
  const getStatusIcon = status => {
    switch (status) {
      case 'Completed':
        return CheckCircleIcon;
      case 'Upcoming':
        return ClockIcon;
      case 'Overdue':
        return XIcon;
      case 'At Risk':
        return AlertTriangleIcon;
      case 'Pending':
        return ClockIcon;
      default:
        return ClockIcon;
    }
  };
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    // In a real app, this would save the milestone to the database
    console.log('Saving milestone:', formData);
    // Show success message
    alert(`Milestone saved successfully!`);
    // Reset form and view
    setShowForm(false);
    setSelectedMilestone(null);
  };
  const handleAddNew = () => {
    setFormData({
      contractId: '',
      title: '',
      description: '',
      dueDate: '',
      completionDate: '',
      status: 'Pending',
      reminderDays: '7',
      notifyUsers: []
    });
    setSelectedMilestone(null);
    setShowForm(true);
  };
  const handleEdit = milestone => {
    setFormData({
      contractId: milestone.contractId,
      title: milestone.title,
      description: milestone.description,
      dueDate: milestone.dueDate,
      completionDate: milestone.completionDate,
      status: milestone.status,
      reminderDays: milestone.reminderDays.toString(),
      notifyUsers: milestone.notifyUsers
    });
    setSelectedMilestone(milestone);
    setShowForm(true);
  };
  const handleMarkComplete = id => {
    // In a real app, this would update the milestone status in the database
    console.log('Marking milestone complete:', id);
    alert(`Milestone ${id} marked as complete`);
  };
  return <div data-id="element-2544">
      <div className="mb-8 flex justify-between items-center" data-id="element-2545">
        <div data-id="element-2546">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-2547">
            Contract Milestones
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2548">
            Track important dates and deliverables for your contracts
          </p>
        </div>
        <button onClick={handleAddNew} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-2549">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-2550" />
          Add Milestone
        </button>
      </div>
      {showForm ? <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-2551">
          <form onSubmit={handleSubmit} className="p-6" data-id="element-2552">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-2553">
              {/* Contract Selection */}
              <div className="sm:col-span-3" data-id="element-2554">
                <label htmlFor="contractId" className="block text-sm font-medium text-gray-700" data-id="element-2555">
                  Contract
                </label>
                <div className="mt-1" data-id="element-2556">
                  <select id="contractId" name="contractId" value={formData.contractId} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2557">
                    <option value="" data-id="element-2558">Select a contract</option>
                    <option value="CTR-2023-001" data-id="element-2559">
                      CTR-2023-001 - Office Lease Agreement
                    </option>
                    <option value="CTR-2023-002" data-id="element-2560">
                      CTR-2023-002 - IT Support Services
                    </option>
                    <option value="CTR-2023-006" data-id="element-2561">
                      CTR-2023-006 - Consulting Services
                    </option>
                    <option value="CTR-2023-008" data-id="element-2562">
                      CTR-2023-008 - Security Services
                    </option>
                    <option value="CTR-2023-010" data-id="element-2563">
                      CTR-2023-010 - Cloud Infrastructure
                    </option>
                  </select>
                </div>
              </div>
              {/* Milestone Title */}
              <div className="sm:col-span-3" data-id="element-2564">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700" data-id="element-2565">
                  Milestone Title
                </label>
                <div className="mt-1" data-id="element-2566">
                  <input type="text" name="title" id="title" value={formData.title} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2567" />
                </div>
              </div>
              {/* Description */}
              <div className="sm:col-span-6" data-id="element-2568">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-2569">
                  Description
                </label>
                <div className="mt-1" data-id="element-2570">
                  <textarea id="description" name="description" rows={2} value={formData.description} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2571" />
                </div>
              </div>
              {/* Due Date */}
              <div className="sm:col-span-3" data-id="element-2572">
                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700" data-id="element-2573">
                  Due Date
                </label>
                <div className="mt-1" data-id="element-2574">
                  <input type="date" name="dueDate" id="dueDate" value={formData.dueDate} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2575" />
                </div>
              </div>
              {/* Completion Date */}
              <div className="sm:col-span-3" data-id="element-2576">
                <label htmlFor="completionDate" className="block text-sm font-medium text-gray-700" data-id="element-2577">
                  Completion Date
                </label>
                <div className="mt-1" data-id="element-2578">
                  <input type="date" name="completionDate" id="completionDate" value={formData.completionDate} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2579" />
                </div>
              </div>
              {/* Status */}
              <div className="sm:col-span-3" data-id="element-2580">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-2581">
                  Status
                </label>
                <div className="mt-1" data-id="element-2582">
                  <select id="status" name="status" value={formData.status} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2583">
                    <option value="Pending" data-id="element-2584">Pending</option>
                    <option value="Upcoming" data-id="element-2585">Upcoming</option>
                    <option value="At Risk" data-id="element-2586">At Risk</option>
                    <option value="Overdue" data-id="element-2587">Overdue</option>
                    <option value="Completed" data-id="element-2588">Completed</option>
                  </select>
                </div>
              </div>
              {/* Reminder Days */}
              <div className="sm:col-span-3" data-id="element-2589">
                <label htmlFor="reminderDays" className="block text-sm font-medium text-gray-700" data-id="element-2590">
                  Send Reminder (days before due date)
                </label>
                <div className="mt-1" data-id="element-2591">
                  <select id="reminderDays" name="reminderDays" value={formData.reminderDays} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2592">
                    <option value="1" data-id="element-2593">1 day</option>
                    <option value="3" data-id="element-2594">3 days</option>
                    <option value="7" data-id="element-2595">1 week</option>
                    <option value="14" data-id="element-2596">2 weeks</option>
                    <option value="30" data-id="element-2597">1 month</option>
                    <option value="60" data-id="element-2598">2 months</option>
                    <option value="90" data-id="element-2599">3 months</option>
                  </select>
                </div>
              </div>
              {/* Notify Users */}
              <div className="sm:col-span-6" data-id="element-2600">
                <label htmlFor="notifyUsers" className="block text-sm font-medium text-gray-700" data-id="element-2601">
                  Notify Users (comma separated emails)
                </label>
                <div className="mt-1" data-id="element-2602">
                  <input type="text" name="notifyUsers" id="notifyUsers" value={formData.notifyUsers.join(', ')} onChange={e => {
                const emails = e.target.value.split(',').map(email => email.trim()).filter(email => email);
                setFormData({
                  ...formData,
                  notifyUsers: emails
                });
              }} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="user1@example.com, user2@example.com" data-id="element-2603" />
                </div>
              </div>
            </div>
            <div className="pt-5 mt-6 border-t border-gray-200" data-id="element-2604">
              <div className="flex justify-end" data-id="element-2605">
                <button type="button" onClick={() => setShowForm(false)} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none" data-id="element-2606">
                  Cancel
                </button>
                <button type="submit" className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-2607">
                  {selectedMilestone ? 'Update Milestone' : 'Create Milestone'}
                </button>
              </div>
            </div>
          </form>
        </div> : <>
          {/* Search */}
          <div className="mb-6" data-id="element-2608">
            <div className="max-w-md" data-id="element-2609">
              <div className="relative rounded-md shadow-sm" data-id="element-2610">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2611">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2612" />
                </div>
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search milestones..." data-id="element-2613" />
              </div>
            </div>
          </div>
          {/* Milestones Stats */}
          <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2614">
            <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-2615">
              <div className="px-4 py-5 sm:p-6" data-id="element-2616">
                <div className="flex items-center" data-id="element-2617">
                  <div className="flex-shrink-0 bg-green-100 rounded-md p-3" data-id="element-2618">
                    <CheckCircleIcon className="h-6 w-6 text-green-600" data-id="element-2619" />
                  </div>
                  <div className="ml-5 w-0 flex-1" data-id="element-2620">
                    <dl data-id="element-2621">
                      <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-2622">
                        Completed
                      </dt>
                      <dd data-id="element-2623">
                        <div className="text-lg font-medium text-gray-900" data-id="element-2624">
                          {milestones.filter(m => m.status === 'Completed').length}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-2625">
              <div className="px-4 py-5 sm:p-6" data-id="element-2626">
                <div className="flex items-center" data-id="element-2627">
                  <div className="flex-shrink-0 bg-blue-100 rounded-md p-3" data-id="element-2628">
                    <ClockIcon className="h-6 w-6 text-blue-600" data-id="element-2629" />
                  </div>
                  <div className="ml-5 w-0 flex-1" data-id="element-2630">
                    <dl data-id="element-2631">
                      <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-2632">
                        Upcoming
                      </dt>
                      <dd data-id="element-2633">
                        <div className="text-lg font-medium text-gray-900" data-id="element-2634">
                          {milestones.filter(m => m.status === 'Upcoming').length}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-2635">
              <div className="px-4 py-5 sm:p-6" data-id="element-2636">
                <div className="flex items-center" data-id="element-2637">
                  <div className="flex-shrink-0 bg-yellow-100 rounded-md p-3" data-id="element-2638">
                    <AlertTriangleIcon className="h-6 w-6 text-yellow-600" data-id="element-2639" />
                  </div>
                  <div className="ml-5 w-0 flex-1" data-id="element-2640">
                    <dl data-id="element-2641">
                      <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-2642">
                        At Risk
                      </dt>
                      <dd data-id="element-2643">
                        <div className="text-lg font-medium text-gray-900" data-id="element-2644">
                          {milestones.filter(m => m.status === 'At Risk').length}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-2645">
              <div className="px-4 py-5 sm:p-6" data-id="element-2646">
                <div className="flex items-center" data-id="element-2647">
                  <div className="flex-shrink-0 bg-red-100 rounded-md p-3" data-id="element-2648">
                    <XIcon className="h-6 w-6 text-red-600" data-id="element-2649" />
                  </div>
                  <div className="ml-5 w-0 flex-1" data-id="element-2650">
                    <dl data-id="element-2651">
                      <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-2652">
                        Overdue
                      </dt>
                      <dd data-id="element-2653">
                        <div className="text-lg font-medium text-gray-900" data-id="element-2654">
                          {milestones.filter(m => m.status === 'Overdue').length}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Milestones Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-2655">
            <div className="overflow-x-auto" data-id="element-2656">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-2657">
                <thead className="bg-gray-50" data-id="element-2658">
                  <tr data-id="element-2659">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2660">
                      Milestone
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2661">
                      Contract
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2662">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2663">
                      Dates
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2664">
                      Reminder
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2665">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2666">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-2667">
                  {filteredMilestones.length === 0 ? <tr data-id="element-2668">
                      <td colSpan={7} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-2669">
                        No milestones found matching your criteria.
                      </td>
                    </tr> : filteredMilestones.map(milestone => {
                const StatusIcon = getStatusIcon(milestone.status);
                return <tr key={milestone.id} className="hover:bg-gray-50" data-id="element-2670">
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2671">
                            <div className="flex items-center" data-id="element-2672">
                              <div data-id="element-2673">
                                <div className="text-sm font-medium text-indigo-600" data-id="element-2674">
                                  {milestone.id}
                                </div>
                                <div className="text-sm text-gray-500" data-id="element-2675">
                                  {milestone.title}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2676">
                            <div className="text-sm text-gray-900" data-id="element-2677">
                              {milestone.contractId}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-2678">
                              {milestone.contractName}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-2679">
                              {milestone.vendor}
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-2680">
                            <div className="text-sm text-gray-900 max-w-xs truncate" data-id="element-2681">
                              {milestone.description}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2682">
                            <div className="flex items-center" data-id="element-2683">
                              <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-2684" />
                              <span data-id="element-2685">Due: {milestone.dueDate}</span>
                            </div>
                            {milestone.completionDate && <div className="flex items-center mt-1" data-id="element-2686">
                                <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-2687" />
                                <span data-id="element-2688">
                                  Completed: {milestone.completionDate}
                                </span>
                              </div>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2689">
                            <div className="flex items-center" data-id="element-2690">
                              <BellIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-2691" />
                              <span data-id="element-2692">{milestone.reminderDays} days before</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-2693">
                              {milestone.notifyUsers.length} users notified
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2694">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(milestone.status)}`} data-id="element-2695">
                              <StatusIcon className="h-3 w-3 mr-1" data-id="element-2696" />
                              {milestone.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2697">
                            <div className="flex justify-end space-x-2" data-id="element-2698">
                              <button onClick={() => handleEdit(milestone)} className="text-indigo-600 hover:text-indigo-900" data-id="element-2699">
                                Edit
                              </button>
                              {milestone.status !== 'Completed' && <button onClick={() => handleMarkComplete(milestone.id)} className="text-green-600 hover:text-green-900" title="Mark as Complete" data-id="element-2700">
                                  <CheckIcon className="h-4 w-4" data-id="element-2701" />
                                </button>}
                            </div>
                          </td>
                        </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>
        </>}
    </div>;
}